import { useState } from 'react'
import {
  Card, Row, Col, Statistic, Select, Divider, Table, Tag, Space,
} from 'antd'
import {
  RiseOutlined, FallOutlined, DollarOutlined, ShoppingCartOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const fmt = (v: number) =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const fmtKg = (v: number) =>
  `${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`

const fmtPerKg = (v: number) =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kg`

const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = dayjs().year() - 2 + i
  return { label: String(y), value: y }
})

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  label: dayjs().month(i).format('MMMM'),
  value: i + 1,
}))

const budgetColumns = [
  { title: 'Description', dataIndex: 'description', key: 'description' },
  {
    title: 'Type', dataIndex: 'budget_type', key: 'budget_type',
    render: (v: string) => <Tag>{v}</Tag>,
  },
  {
    title: 'Budgeted (₹)', dataIndex: 'budgeted_amount', key: 'budgeted_amount',
    align: 'right' as const,
    render: (v: number) => fmt(v),
  },
  {
    title: 'Actual (₹)', dataIndex: 'actual_amount', key: 'actual_amount',
    align: 'right' as const,
    render: (v: number) => fmt(v),
  },
  {
    title: 'Variance (₹)', dataIndex: 'variance', key: 'variance',
    align: 'right' as const,
    render: (v: number) => (
      <span style={{ color: v < 0 ? '#ff4d4f' : '#52c41a', fontWeight: 500 }}>
        {fmt(v)}
      </span>
    ),
  },
  {
    title: 'Variance %', dataIndex: 'variance_pct', key: 'variance_pct',
    align: 'right' as const,
    render: (v: number) => (
      <Tag color={v < 0 ? 'red' : 'green'}>
        {Number(v ?? 0).toFixed(1)}%
      </Tag>
    ),
  },
]

export default function MISDashboardPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [year, setYear] = useState(dayjs().year())
  const [month, setMonth] = useState(dayjs().month() + 1)

  const { data: dashboard, isLoading: dashLoading } = useQuery({
    queryKey: ['mis-dashboard', plantId, year, month],
    queryFn: () =>
      apiClient.get('/mis/dashboard', {
        params: { plant_id: plantId, year, month },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: budgetData, isLoading: budgetLoading } = useQuery({
    queryKey: ['mis-budget-variance', plantId, year],
    queryFn: () =>
      apiClient.get('/mis/budget-variance', {
        params: { plant_id: plantId, budget_year: year },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: copData, isLoading: copLoading } = useQuery({
    queryKey: ['mis-cop', plantId, year, month],
    queryFn: () =>
      apiClient.get('/mis/cost-of-production', {
        params: { plant_id: plantId, year, month },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const d = dashboard ?? {}
  const cop = copData ?? {}
  const budgetRows = Array.isArray(budgetData) ? budgetData : budgetData?.items ?? []

  const headerExtra = (
    <Space wrap>
      <Select
        style={{ width: 180 }}
        placeholder="Select Plant"
        value={plantId || undefined}
        onChange={setPlantId}
        options={plants.map((p: any) => ({ label: p.name, value: p.id }))}
      />
      <Select
        style={{ width: 110 }}
        value={year}
        onChange={setYear}
        options={YEAR_OPTIONS}
      />
      <Select
        style={{ width: 120 }}
        value={month}
        onChange={setMonth}
        options={MONTH_OPTIONS}
      />
    </Space>
  )

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="MIS Dashboard"
        subtitle="Management KPI overview — sales, purchases, outstanding & production"
        extra={headerExtra}
      />

      {/* Row 1 — Financial highlights */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Total Sales"
              value={d.total_sales ?? 0}
              precision={2}
              prefix={<RiseOutlined />}
              formatter={v => fmt(Number(v))}
              valueStyle={{ color: '#52c41a' }}
              loading={dashLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Total Purchases"
              value={d.total_purchases ?? 0}
              precision={2}
              prefix={<ShoppingCartOutlined />}
              formatter={v => fmt(Number(v))}
              valueStyle={{ color: '#fa8c16' }}
              loading={dashLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="AR Outstanding"
              value={d.ar_total ?? 0}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={v => fmt(Number(v))}
              valueStyle={{ color: '#1677ff' }}
              loading={dashLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="AP Outstanding"
              value={d.ap_total ?? 0}
              precision={2}
              prefix={<FallOutlined />}
              formatter={v => fmt(Number(v))}
              valueStyle={{ color: '#ff4d4f' }}
              loading={dashLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 2 — Operational counts */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {[
          { title: 'Open POs', value: d.open_pos ?? 0 },
          { title: 'Pending GRNs', value: d.pending_grns ?? 0 },
          { title: 'Open Sales Orders', value: d.open_sales_orders ?? 0 },
          { title: 'Draft Vouchers', value: d.total_vouchers_draft ?? 0 },
          { title: 'Active Cotton Lots', value: d.cotton_lots_active ?? 0 },
        ].map((item, i) => (
          <Col xs={12} sm={8} lg={24 / 5} key={i}>
            <Card size="small">
              <Statistic
                title={item.title}
                value={item.value}
                loading={dashLoading}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Cost of Production */}
      <Divider orientation="left" style={{ marginTop: 24 }}>Cost of Production</Divider>
      <Row gutter={[16, 16]}>
        {[
          { title: 'RM Cost / kg', value: cop.rm_cost_per_kg, fmt: fmtPerKg },
          { title: 'Conversion Cost / kg', value: cop.conversion_cost_per_kg, fmt: fmtPerKg },
          { title: 'Total Cost / kg', value: cop.total_cost_per_kg, fmt: fmtPerKg },
          { title: 'Total Production', value: cop.total_production_kg, fmt: fmtKg },
        ].map((item, i) => (
          <Col xs={24} sm={12} lg={6} key={i}>
            <Card size="small">
              <Statistic
                title={item.title}
                value={item.value ?? 0}
                formatter={v => item.fmt(Number(v))}
                loading={copLoading}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Budget Variance */}
      <Divider orientation="left" style={{ marginTop: 24 }}>Budget Summary</Divider>
      <Card size="small">
        <Table
          dataSource={budgetRows}
          columns={budgetColumns}
          rowKey={(r: any) => r.id ?? r.description}
          loading={budgetLoading}
          size="small"
          pagination={{ pageSize: 15 }}
          scroll={{ x: 800 }}
          locale={{ emptyText: 'No budget data for this year' }}
        />
      </Card>
    </div>
  )
}
